// The sound menu's arithmetic (MixerControls.tsx): where it opens, what a
// search keeps and how its rows group. Pure, so it is tested without a layout
// engine.

export interface SoundOption {
  id: string;
  label: string;
  group?: string;
}

/** A long list's search field (the drum loops). */
export interface SoundMenuSearch {
  /** The field's name, read aloud and shown as its placeholder. */
  label: string;
  /** Shown when nothing matches. */
  empty: string;
}

export interface MenuAnchor {
  top: number;
  bottom: number;
  left: number;
}

/** The part of the screen a person can see: on a phone, above its keyboard. */
export interface VisibleArea {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface MenuPlacement {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
}

const MARGIN = 8;
const GAP = 6;

/**
 * Below the trigger when the menu's content fits there or there is at least as
 * much room below as above, else above, and never past the visible area's
 * edges. A trigger the visible area no longer shows (a phone's keyboard has
 * come up over it) keeps the menu at the nearest edge, not under the keyboard.
 */
export function placeMenu(
  anchor: MenuAnchor,
  area: VisibleArea,
  content: number,
  limits: { maxHeight: number; maxWidth: number },
): MenuPlacement {
  const visibleTop = area.top + MARGIN;
  const visibleBottom = area.top + area.height - MARGIN;
  const clamp = (value: number) => Math.min(visibleBottom, Math.max(visibleTop, value));
  const downTop = clamp(anchor.bottom + GAP);
  const upBottom = clamp(anchor.top - GAP);
  const roomBelow = visibleBottom - downTop;
  const roomAbove = upBottom - visibleTop;
  const wanted = Math.min(content, limits.maxHeight);
  const down = roomBelow >= wanted || roomBelow >= roomAbove;
  const maxHeight = Math.max(0, Math.min(limits.maxHeight, down ? roomBelow : roomAbove));
  const width = Math.min(limits.maxWidth, area.width - 2 * MARGIN);
  return {
    top: down ? downTop : upBottom - Math.min(wanted, maxHeight),
    left: Math.max(
      area.left + MARGIN,
      Math.min(anchor.left, area.left + area.width - width - MARGIN),
    ),
    width,
    maxHeight,
  };
}

/** Text as a search reads it, folded the way the tune search folds it: case,
 * accents and punctuation aside, so "baiao" finds Baião and "cha cha" finds
 * Cha-cha. */
function fold(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/['’‘ʼ＇`´]/gu, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

/** The rows a search keeps: every word of it somewhere in the row's name or
 * its section, so "swing 132" finds the swing loops at ♩=132. */
export function searchSoundOptions<T extends SoundOption>(
  options: readonly T[],
  query: string,
): readonly T[] {
  const terms = fold(query).split(' ').filter(Boolean);
  if (terms.length === 0) return options;
  return options.filter((option) => {
    const text = fold(`${option.label} ${option.group ?? ''}`);
    return terms.every((term) => text.includes(term));
  });
}

export interface SoundGroup<T extends SoundOption> {
  /** The heading; absent for rows with no section. */
  name?: string;
  options: T[];
}

/** Rows under their headings, each heading once, in the order the sections
 * first appear. The menu used to head every change of section, so a list not
 * sorted by section repeated its headings (the drum loops headed Afro-Cuban
 * 29 times). */
export function groupSoundOptions<T extends SoundOption>(options: readonly T[]): SoundGroup<T>[] {
  const groups = new Map<string | undefined, SoundGroup<T>>();
  for (const option of options) {
    const group = groups.get(option.group) ?? { name: option.group, options: [] };
    group.options.push(option);
    groups.set(option.group, group);
  }
  return [...groups.values()];
}
